<template>
  <v-container>
    <v-alert v-if="noProductsError" type="error" prominent class="mt-4">
      Your account is not configured for any LME brand product. Please contact
      the administrator to set it up.
    </v-alert>
    <v-card v-else>
      <v-card-title>
        LME Passport Management
        <v-spacer></v-spacer>
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          label="Filter by Status"
          clearable
          class="ml-4"
          style="max-width: 200px"
        ></v-select>
        <v-btn
          color="primary"
          @click="syncStatuses"
          class="ml-4"
          :loading="loadingSync"
          :disabled="loadingSync"
          >Sync Statuses</v-btn
        >
      </v-card-title>
      <v-card-subtitle>
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                @input="startDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                @input="endDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              @click="fetchInventories"
              class="mt-4"
              :loading="loading"
              :disabled="loading"
              >Filter</v-btn
            >
          </v-col>
        </v-row>
      </v-card-subtitle>

      <div class="mx-4 my-2">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        ></v-text-field>
      </div>
      <v-data-table
        :headers="headers"
        :items="inventories"
        :search="search"
        :loading="loading"
        loading-text="Loading... Please wait"
        :items-per-page="10"
      >
        <template v-slot:item.inventoryId="{ item }">
          <a
            v-if="item.inventoryId"
            @click="openInventoryLookup(item)"
            class="inventory-link"
            >{{ item.inventoryId }}</a
          >
          <span v-else>N/A</span>
        </template>
        <template v-slot:item.coaReferenceNumber="{ item }">
          <span class="mr-2">{{ item.coaReferenceNumber }}</span>
          <v-tooltip v-if="!item.hasLabResult" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="warning" v-bind="attrs" v-on="on"
                >mdi-alert</v-icon
              >
            </template>
            <span>No lab result available</span>
          </v-tooltip>
          <v-tooltip v-else-if="!item.signed" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="error" v-bind="attrs" v-on="on"
                >mdi-alert-circle</v-icon
              >
            </template>
            <span>CoA not signed</span>
          </v-tooltip>
          <v-tooltip v-else-if="item.hasCompositionExceeded" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="error" v-bind="attrs" v-on="on"
                >mdi-alert-octagon</v-icon
              >
            </template>
            <span>Composition exceeds LME standards</span>
          </v-tooltip>
          <v-icon v-else small color="success">mdi-check</v-icon>
        </template>
        <template v-slot:item.coaDate="{ item }">
          {{ formatDate(item.coaDate) }}
        </template>
        <template v-slot:item.quality="{ item }">
          <span>{{ item.quality || "N/A" }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-2"
                @click="previewPayload(item.coaReferenceNumber)"
                :disabled="loadingActions[item.coaReferenceNumber]"
                v-bind="attrs"
                v-on="on"
                >mdi-file-document-outline</v-icon
              >
            </template>
            <span>Preview Payload</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-2"
                @click="previewPDF(item.coaReferenceNumber)"
                :disabled="loadingActions[item.coaReferenceNumber]"
                v-bind="attrs"
                v-on="on"
                >mdi-file-pdf-box</v-icon
              >
            </template>
            <span>Preview PDF</span>
          </v-tooltip>
          <v-tooltip top v-if="item.parentDocumentId">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-2"
                @click="viewRecord(item)"
                :disabled="loadingActions[item.coaReferenceNumber]"
                v-bind="attrs"
                v-on="on"
                >mdi-eye</v-icon
              >
            </template>
            <span>View Record</span>
          </v-tooltip>
          <v-tooltip
            top
            v-if="
              (item.status === 'NOT_SUBMITTED' ||
                item.status === 'DRAFT' ||
                item.status === 'ERROR')
            "
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-2"
                @click="submitRecord(item)"
                :disabled="
                  !item.hasLabResult || !item.signed || loadingActions[item.coaReferenceNumber]
                "
                v-bind="attrs"
                v-on="on"
                >mdi-upload</v-icon
              >
            </template>
            <span>Submit Record (requires lab result and signing)</span>
          </v-tooltip>
          <v-tooltip
            top
            v-if="item.parentDocumentId && (!(item.status == 'APPROVED' || item.status == 'PENDING_APPROVAL'))"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-2"
                @click="uploadDocument(item)"
                :disabled="loadingActions[item.coaReferenceNumber] || !item.signed"
                v-bind="attrs"
                v-on="on"
                >mdi-file-upload</v-icon
              >
            </template>
            <span>{{ item.signed ? 'Upload Document' : 'Upload Document (requires signing)' }}</span>
          </v-tooltip>
          <v-tooltip
            top
            v-if="item.status === 'SUBMITTED' || item.status === 'APPROVED'"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                @click="deleteRecord(item)"
                :disabled="loadingActions[item.coaReferenceNumber]"
                v-bind="attrs"
                v-on="on"
                >mdi-delete</v-icon
              >
            </template>
            <span>Delete Record</span>
          </v-tooltip>
          <v-progress-circular
            v-if="loadingActions[item.coaReferenceNumber]"
            indeterminate
            size="16"
            width="2"
            color="primary"
            class="ml-2"
          ></v-progress-circular>
        </template>
      </v-data-table>
    </v-card>

    <!-- Preview JSON Dialog -->
    <v-dialog v-model="showJsonPreview" max-width="800">
      <v-card>
        <v-card-title>JSON Payload Preview</v-card-title>
        <v-card-text>
          <v-checkbox
            v-model="includeEncodedCoaText"
            label="Include Encoded CoA Text"
          ></v-checkbox>
          <pre>{{ jsonPreview }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showJsonPreview = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview PDF Dialog -->
    <v-dialog v-model="showPdfPreview" max-width="800" max-height="600">
      <v-card>
        <v-card-title>
          PDF Preview
          <v-spacer></v-spacer>
          <v-btn
            v-if="
              selectedItem &&
              selectedItem.status === 'SUBMITTED_MISSING_DOCUMENT'
            "
            color="primary"
            text
            @click="uploadDocument(selectedItem)"
            :disabled="selectedItem && (loadingActions[selectedItem.coaReferenceNumber] || !selectedItem.signed)"
          >
            {{ selectedItem.signed ? 'Upload PDF' : 'Upload PDF (requires signing)' }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <iframe
            :src="pdfPreviewUrl"
            width="100%"
            height="500px"
            v-if="pdfPreviewUrl"
          ></iframe>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showPdfPreview = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Record Details Dialog -->
    <v-dialog v-model="showRecordDetails" max-width="800">
      <v-card>
        <v-card-title>Record Details</v-card-title>
        <v-card-subtitle
          >Note: We can only view approved record from LME
          Passport</v-card-subtitle
        >
        <v-card-text>
          <pre>{{ recordDetails }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showRecordDetails = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Inventory Lookup Dialog -->
    <inventory-lookup
      :activated.sync="lookupInventory"
      v-model="selectedInventoryId"
      @selected="handleInventorySelected"
    />
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment"; // Import moment for date formatting and manipulation
const InventoryLookup = () =>
  import("@/components/inventory/InventoryLookup.vue");

export default {
  data() {
    return {
      inventories: [],
      loading: false,
      search: "",
      statusFilter: null,
      statusOptions: [
        "NOT_SUBMITTED",
        "SUBMITTED",
        "SUBMITTED_MISSING_DOCUMENT",
        "PENDING_APPROVAL",
        "APPROVED",
        "ERROR",
      ],
      headers: [
        { text: "Inventory ID", value: "inventoryId", sortable: true },
        { text: "CoA Reference", value: "coaReferenceNumber" },
        { text: "Document Title", value: "documentTitle" },
        { text: "CoA Date", value: "coaDate" },
        { text: "Status", value: "status" },
        { text: "Weight", value: "weight" },
        { text: "Quality", value: "quality" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      showJsonPreview: false,
      jsonPreview: "",
      showPdfPreview: false,
      pdfPreviewUrl: "",
      showRecordDetails: false,
      recordDetails: "",
      showUpdateDialog: false,
      updateForm: {
        quantity: "",
        totalGrossWeight: "",
        totalNetWeight: "",
      },
      updateFormValid: false,
      selectedItem: null,
      productIds: [],
      productNames: [], // Store fetched product names
      noProductsError: false, // Flag for no product names error
      startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      startDateMenu: false,
      endDateMenu: false,
      lookupInventory: false,
      selectedInventoryId: null,
      loadingActions: {}, // Track loading state for each row's actions
      loadingSync: false, // Track loading state for syncStatuses
      includeEncodedCoaText: false,
      selectedCoa: null,
    };
  },
  components: {
    InventoryLookup,
  },
  async mounted() {
    await this.fetchProductNamesAndIds();
    if (!this.noProductsError) {
      this.fetchInventories();
    }
  },
  methods: {
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    formatDate(timestamp) {
      if (!timestamp) return "";
      return moment(timestamp).format("YYYY-MM-DD");
    },
    async fetchProductNamesAndIds() {
      try {
        // Fetch product names from the new endpoint
        const response = await this.$axios.get("/api/lme/records/brands");
        // Safely map the response to get an array of product names
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          this.productNames = response.data.map((item) => item.internalProductName);
        } else {
          this.productNames = [];
        }

        if (this.productNames.length === 0) {
          this.noProductsError = true;
          this.addErrorMessage(
            "Your account is not configured for any LME brand product. Please contact the administrator to set it up."
          );
          return;
        }

        // Fetch product IDs using the product names
        const productNamesQuery = this.productNames.join(",");
        const productIdsResponse = await this.$axios.get(
          "/category/searchByProductNames",
          {
            params: { productNames: productNamesQuery },
          }
        );
        this.productIds = productIdsResponse.data.data.map((item) => item.id);

        if (this.productIds.length === 0) {
          this.noProductsError = true;
          this.addErrorMessage(
            "Your account is not configured for any LME brand product. Please contact the administrator to set it up."
          );
        }
      } catch (error) {
        console.error("Error fetching LME brand product names or IDs:", error);
        this.noProductsError = true;
        this.addErrorMessage(
          "Your account is not configured for any LME brand product. Please contact the administrator to set it up."
        );
      }
    },
    async fetchInventories() {
      if (this.noProductsError) return; // Skip if no products
      this.loading = true;
      try {
        const response = await this.$axios.get("/api/lme/records/inventories", {
          params: {
            productIds: this.productIds.join(","),
            status: this.statusFilter,
            startDate: this.startDate,
            endDate: this.endDate,
          },
        });
        this.inventories = response.data;
      } catch (error) {
        console.error("Error fetching inventories:", error);
        this.addErrorMessage("Error fetching LME inventories");
      } finally {
        this.loading = false;
      }
    },
    async previewPayload(coa) {
      this.includeEncodedCoaText = false;
      this.selectedCoa = coa;
      await this.fetchJsonPreview();
      this.showJsonPreview = true;
    },
    async fetchJsonPreview() {
      const coa = this.selectedCoa;
      this.$set(this.loadingActions, coa, true);
      try {
        const response = await this.$axios.get(
          `/api/lme/records/preview/payload/${coa}`,
          {
            params: { includeEncodedCoaText: this.includeEncodedCoaText },
          }
        );
        this.jsonPreview = JSON.stringify(response.data, null, 2);
      } catch (error) {
        console.error("Error previewing payload:", error);
        this.addErrorMessage("Error previewing payload");
      } finally {
        this.$set(this.loadingActions, coa, false);
      }
    },
    async previewPDF(coa) {
      this.$set(this.loadingActions, coa, true);
      try {
        const response = await this.$axios.get(
          `/api/lme/records/preview/pdf/${coa}`,
          {
            responseType: "blob",
          }
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        this.pdfPreviewUrl = URL.createObjectURL(blob);
        // Find the item to set as selectedItem
        this.selectedItem = this.inventories.find(
          (item) => item.coaReferenceNumber === coa
        );
        this.showPdfPreview = true;
      } catch (error) {
        console.error("Error previewing PDF:", error);
        this.addErrorMessage("Error previewing PDF");
      } finally {
        this.$set(this.loadingActions, coa, false);
      }
    },
    async viewRecord(item) {
      this.$set(this.loadingActions, item.coaReferenceNumber, true);
      try {
        const response = await this.$axios.get(
          `/api/lme/records/${item.coaReferenceNumber}`
        );
        this.recordDetails = JSON.stringify(response.data, null, 2);
        this.showRecordDetails = true;
      } catch (error) {
        console.error("Error viewing record:", error);
        this.addErrorMessage("Error viewing record");
      } finally {
        this.$set(this.loadingActions, item.coaReferenceNumber, false);
      }
    },
    async downloadPDF(item) {
      this.$set(this.loadingActions, item.coaReferenceNumber, true);
      try {
        const response = await this.$axios.get(
          `/api/lme/records/download/pdf/${item.parentDocumentId}`,
          {
            params: { coaReference: item.coaReferenceNumber },
            responseType: "blob",
          }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${item.coaReferenceNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        this.addSuccessMessage("PDF downloaded successfully");
      } catch (error) {
        console.error("Error downloading PDF:", error);
        this.addErrorMessage("Error downloading PDF");
      } finally {
        this.$set(this.loadingActions, item.coaReferenceNumber, false);
      }
    },
    async submitRecord(item) {
      if (!item.hasLabResult || !item.signed) {
        this.addErrorMessage("Cannot submit record without lab result or signing");
        return;
      }
      this.$set(this.loadingActions, item.coaReferenceNumber, true);
      try {
        const response = await this.$axios.post(
          `/api/lme/records/${item.coaReferenceNumber}`
        );
        console.log("Submitted, passportId:", response.data);
        this.addSuccessMessage("Record submitted successfully");
        this.fetchInventories();
      } catch (error) {
        console.error("Error submitting record:", error);
        this.addErrorMessage("Error submitting record");
      } finally {
        this.$set(this.loadingActions, item.coaReferenceNumber, false);
      }
    },
    async uploadDocument(item) {
      this.$set(this.loadingActions, item.coaReferenceNumber, true);
      try {
        await this.$axios.post(
          `/api/lme/records/upload/pdf/${item.coaReferenceNumber}`
        );
        this.addSuccessMessage("Document uploaded successfully");
        this.fetchInventories();
        this.showPdfPreview = false; // Close the PDF preview dialog
      } catch (error) {
        const errorMessage =
          "Record not ready for uploading document, please try again later";
        this.addErrorMessage(errorMessage);
      } finally {
        this.$set(this.loadingActions, item.coaReferenceNumber, false);
      }
    },
    async deleteRecord(item) {
      if (confirm("Are you sure you want to delete?")) {
        this.$set(this.loadingActions, item.coaReferenceNumber, true);
        try {
          await this.$axios.delete(
            `/api/lme/records/${item.coaReferenceNumber}`
          );
          this.addSuccessMessage("Record deleted successfully");
          this.fetchInventories();
        } catch (error) {
          console.error("Error deleting record:", error);
          this.addErrorMessage("Error deleting record");
        } finally {
          this.$set(this.loadingActions, item.coaReferenceNumber, false);
        }
      }
    },
    async syncStatuses() {
      this.loadingSync = true;
      try {
        await this.$axios.post("/api/lme/records/sync");
        this.addSuccessMessage("Statuses synced successfully");
        this.fetchInventories();
      } catch (error) {
        console.error("Error syncing statuses:", error);
        this.addErrorMessage("Error syncing statuses");
      } finally {
        this.loadingSync = false;
      }
    },
    openInventoryLookup(item) {
      this.selectedItem = item;
      this.selectedInventoryId = item.inventoryId;
      this.lookupInventory = true;
    },
    handleInventorySelected(inventory) {
      console.log("Selected inventory:", inventory);
      this.addSuccessMessage(`Inventory ${inventory.id} selected`);
      this.lookupInventory = false;
    },
  },
  watch: {
    statusFilter() {
      if (!this.noProductsError) {
        this.fetchInventories();
      }
    },
    includeEncodedCoaText() {
      if (this.showJsonPreview) {
        this.fetchJsonPreview();
      }
    },
  },
};
</script>

<style scoped>
.inventory-link {
  color: #1976d2; /* Vuetify primary blue color */
  text-decoration: underline;
  cursor: pointer;
}
.inventory-link:hover {
  color: #1565c0; /* Darker shade on hover */
}
</style>